import { parseMoneyToCents, type MoneyInput } from './money'

/**
 * Where a franchise delivers, and what it charges to go there.
 *
 * Four screens need this answer — the order builder, the restaurant till, the
 * dispatch board and the zone editor — so it lives here (§5). It is also money
 * and geometry, which is to say it is the part of the order that is wrong
 * silently: a point judged just outside a polygon does not raise an error, it
 * quotes the wrong fee and nobody notices until the month's takings are short.
 *
 * Ported from legacy `lib/delivery-zones-store.ts`, with the fee arithmetic
 * moved to integer cents (§14.7).
 */

export interface LatLng {
  lat: number
  lng: number
}

export interface PriceTier {
  id: string
  /** Inclusive lower bound, in euros as the API sends it. */
  orderFrom: MoneyInput
  /** Exclusive upper bound; `null` means "and everything above". */
  orderTo: MoneyInput | null
  deliveryFee: MoneyInput
}

export interface DeliveryZone {
  id: string
  name: string
  color: string
  enabled: boolean
  type: 'circle' | 'polygon'
  center?: LatLng
  /** Metres. */
  radius?: number
  polygon?: LatLng[]
  minOrder: MoneyInput
  tiers: PriceTier[]
}

const EARTH_RADIUS_METRES = 6_371_000
const MINIMUM_POLYGON_POINTS = 3

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/** Great-circle distance. Zone radii are a few kilometres, so this is exact enough. */
export function haversineMetres(from: LatLng, to: LatLng): number {
  const deltaLat = toRadians(to.lat - from.lat)
  const deltaLng = toRadians(to.lng - from.lng)
  const chordHalf =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(deltaLng / 2) ** 2
  return EARTH_RADIUS_METRES * 2 * Math.atan2(Math.sqrt(chordHalf), Math.sqrt(1 - chordHalf))
}

/**
 * Ray casting, counting crossings of a horizontal ray to the west.
 *
 * A point exactly on an edge is not promised either answer — floating point
 * decides — and that is true of every implementation of this. The zones a
 * franchise draws have streets between them, so it does not arise in practice.
 */
export function isPointInPolygon(point: LatLng, polygon: LatLng[]): boolean {
  let inside = false
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const current = polygon[index]
    const before = polygon[previous]
    if (!current || !before) continue

    const straddles = current.lat > point.lat !== before.lat > point.lat
    if (!straddles) continue

    const crossingLng =
      ((before.lng - current.lng) * (point.lat - current.lat)) / (before.lat - current.lat) +
      current.lng
    if (point.lng < crossingLng) inside = !inside
  }
  return inside
}

/**
 * The first enabled zone containing the point.
 *
 * First, not nearest and not smallest: where two zones overlap, the order of
 * the list decides, and the zone editor's order is therefore a pricing
 * decision. Ported as-is — changing it would re-price every address in an
 * overlap without anyone asking for it.
 */
export function findZoneForPoint(point: LatLng, zones: DeliveryZone[]): DeliveryZone | null {
  for (const zone of zones) {
    if (!zone.enabled) continue

    if (zone.type === 'circle' && zone.center && zone.radius != null) {
      if (haversineMetres(point, zone.center) <= zone.radius) return zone
      continue
    }

    if (
      zone.type === 'polygon' &&
      zone.polygon &&
      zone.polygon.length >= MINIMUM_POLYGON_POINTS &&
      isPointInPolygon(point, zone.polygon)
    ) {
      return zone
    }
  }
  return null
}

/**
 * The delivery fee in cents, or `null` when the order is under the zone's
 * minimum — which means *we do not deliver this*, not *delivery is free*. The
 * caller has to tell those apart, so the two answers are different types.
 *
 * Tiers are half-open: `[orderFrom, orderTo)`. A gap between tiers falls
 * through to the highest tier rather than to zero, because a franchise that
 * mis-enters its bands should over-charge rather than deliver for nothing.
 */
export function deliveryFeeCents(orderTotal: MoneyInput, zone: DeliveryZone): number | null {
  const totalCents = parseMoneyToCents(orderTotal)
  if (totalCents < parseMoneyToCents(zone.minOrder)) return null

  const byLowerBound = [...zone.tiers].sort(
    (left, right) => parseMoneyToCents(left.orderFrom) - parseMoneyToCents(right.orderFrom),
  )

  for (const tier of byLowerBound) {
    const from = parseMoneyToCents(tier.orderFrom)
    const to = tier.orderTo === null || tier.orderTo === undefined ? null : parseMoneyToCents(tier.orderTo)
    if (totalCents >= from && (to === null || totalCents < to)) {
      return parseMoneyToCents(tier.deliveryFee)
    }
  }

  const highest = byLowerBound[byLowerBound.length - 1]
  return highest ? parseMoneyToCents(highest.deliveryFee) : 0
}

/** The palette the zone editor offers. Ported so a re-drawn zone keeps its colour. */
export const ZONE_COLORS = [
  '#3b82f6',
  '#f97316',
  '#22c55e',
  '#a855f7',
  '#ef4444',
  '#eab308',
  '#06b6d4',
  '#ec4899',
]

/*
 * ── Which of several zones an address belongs to - §10A.121, moved here 2026-09-17 ──────────────
 *
 * **It lived in `customer-checkout/src/delivery.ts` and now two apps need it.** §9.346 made the
 * manager's phone-order picker resolve a tie of equally-named zones, and the tie-break has to be the
 * same four steps the checkout and `DeliveryZoneService.pickZone` use - a third rule would be a
 * third delivery fee. `customer-checkout` re-exports it, so every existing import still resolves.
 */
const METRES_PER_DEGREE_FOR_TIES = 111_320

/** Diacritics out, lower-cased, whitespace collapsed - so `Bánov` matches `banov`. */
function fold(value: string | null | undefined): string {
  // Undefined as well as null: a caller may hand this a zone whose `name` its own parser dropped,
  // and a crash inside a tie-break is worse than an empty string - §9.346 found exactly that.
  if (value === null || value === undefined) return ''
  return value
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/** The same flat-degree arithmetic `withinRadius` uses, so the tie-break cannot pick a zone the point is not in. */
function metresToCentre(zone: ZoneTieBreakInput, lat: number, lng: number): number {
  // `DeliveryZone` here declares `center` optional, where the checkout's local type had it nullable -
  // so this guards both, and an unplaceable zone can never win a tie-break it cannot be measured for.
  const centre = zone.center
  if (centre === null || centre === undefined) return Number.MAX_VALUE
  return Math.hypot(lat - centre.lat, lng - centre.lng) * METRES_PER_DEGREE_FOR_TIES
}

/**
 * Which of several containing zones an address belongs to - §10A.121.
 *
 * **The owner gave the rule and then corrected it, and the correction is the one that matters.**
 * First: *"when two doručovacie zóny colides with our address, use that one, who is closer to Adresa
 * depota"*. Then: *"when adresa is in bánov, but we also have zóna overlap from nitriansky hrádok,
 * then use bánov"*. Those are different rules - the second says the **address names the place**, and
 * that beats any distance. Both are wanted, in that order.
 *
 * Measured on their own data: **8 of the 465 zone pairs overlap**, including *Bánov* with *Nitriansky
 * Hrádok* - their example. This function took the **first match in list order** and the server took
 * `created_at` order, so the winner was an accident of insertion order **and the two could disagree
 * about which fee applies**.
 *
 * <p>`DeliveryZoneService.pickZone` is the same four steps in Java and
 * `OverlappingZonesTest` carries the same cases under the same names. Keeping them in step is the
 * whole point: a client and a server that resolve a zone differently is a delivery fee that differs
 * from the one the customer agreed to.
 */
/**
 * All the tie-break needs of a zone: a name to look for in the address, and a circle to measure.
 *
 * **Declared rather than taking `DeliveryZone`**, because the two callers do not share a type -
 * `customer-checkout` has its own, looser one, and the manager builds a partial. Asking for the whole
 * interface made 20 of the checkout's own fixtures stop compiling the moment this moved here, which
 * is a signature demanding more than the function reads.
 */
export interface ZoneTieBreakInput {
  name?: string | null
  center?: LatLng | null
  radius?: number | null
}

export function pickZone<T extends ZoneTieBreakInput>(
  candidates: readonly T[],
  address: string | null,
  depot: { lat: number; lng: number } | null,
): T | null {
  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0] ?? null

  // Step 3 - the address names one of them, the longer name winning where one contains another
  // (§9.106: *"the more specific name is the one the caller bothered to say"*). Three characters
  // minimum, because a two-letter zone name appears in most Slovak addresses by accident.
  const haystack = fold(address)
  let named: T | null = null
  let namedLength = 0
  let contested = false
  if (haystack !== '') {
    for (const zone of candidates) {
      const needle = fold(zone.name)
      if (needle.length < 3 || !haystack.includes(needle)) continue
      if (needle.length > namedLength) {
        named = zone
        namedLength = needle.length
        contested = false
      } else if (needle.length === namedLength) {
        contested = true
      }
    }
  }
  if (named !== null && !contested) return named

  // Step 4 - the depot, then the smallest. Both deterministic; the second needs nothing but the zone
  // rows, so the answer never depends on whether the geocoder was up.
  let best: T | null = null
  let bestKey = Number.MAX_VALUE
  for (const zone of candidates) {
    const key =
      depot === null
        ? (zone.radius ?? Number.MAX_VALUE)
        : metresToCentre(zone, depot.lat, depot.lng)
    if (key < bestKey) {
      bestKey = key
      best = zone
    }
  }
  return best
}

